import React, { useState, useCallback, useEffect } from 'react';
import { BsTelephone, BsGeoAlt, BsBuilding } from 'react-icons/bs';
import { useAuth } from '../../context/AuthContext';
import './Visualization.css';

const VIZ_API_URL = 'https://acm5sxiiak.execute-api.us-east-1.amazonaws.com/prod/vizualization';

const MENU_ITEMS = [
  { id: 'tasks', label: 'Tasks' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'notes', label: 'Notes' },
  { id: 'just_saved', label: 'Just Saved' },
];

const KANBAN_COLUMNS = [
  { id: 'personal', label: 'Personal' },
  { id: 'official', label: 'Official' },
  { id: 'business', label: 'Business' },
  { id: 'other', label: 'Other' },
];

const CONTACT_COLUMNS = [
  { id: 'personal', label: 'Personal' },
  { id: 'business', label: 'Business' },
  { id: 'other', label: 'Other' },
];

const EXPENSE_CATEGORIES = [
  { id: 'medical', label: 'Medical' },
  { id: 'food', label: 'Food' },
  { id: 'vehicle', label: 'Vehicle' },
  { id: 'other', label: 'Other' },
];

const EMPTY_BOARD = {
  personal: [],
  official: [],
  business: [],
  other: [],
};

const JUST_SAVED_PANELS = [
  { id: 'tasks', label: 'Tasks' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'notes', label: 'Notes' },
];

const EMPTY_JUST_SAVED_PANELS = { tasks: [], contacts: [], expenses: [], notes: [] };

const EMPTY_BOARD_CONTACTS = {
  personal: [],
  business: [],
  other: [],
};

const EMPTY_BOARD_TASKS = { items: [] };

const EMPTY_BOARD_NOTES = { items: [] };

const EMPTY_BOARD_EXPENSES = {
  medical: [],
  food: [],
  vehicle: [],
  other: [],
};

function contactToTile(item, panelId, index) {
  return {
    id: panelId + '-' + index + '-' + (item.phone || item.name || '').replace(/\s/g, '_'),
    title: item.name || 'Untitled',
    designation: item.designation,
    company: item.company,
    address: item.address,
    phone: item.phone,
    s3_link: item.s3_link,
  };
}

function expenseToTile(item, panelId, index) {
  var title = item.vendor || item.description || (item.amount != null ? String(item.amount) + (item.currency ? ' ' + item.currency : '') : 'Expense');
  return {
    id: panelId + '-' + index + '-' + (item.vendor || item.amount || index).toString().replace(/\s/g, '_'),
    title: title,
    designation: item.category || null,
    company: item.amount != null ? String(item.amount) + (item.currency ? ' ' + item.currency : '') : null,
    address: item.description || null,
    phone: item.contact_name || null,
    s3_link: item.s3_link || null,
  };
}

function expenseCategoryFromItem(item) {
  var c = (item.category || 'other').toString().toLowerCase();
  if (c === 'medical') return 'medical';
  if (c === 'food') return 'food';
  if (c === 'vehicle') return 'vehicle';
  return 'other';
}

function formatNoteDate(timestamp) {
  if (!timestamp) return null;
  var s = String(timestamp).trim();
  if (!s) return null;
  var match = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  var d = new Date(s);
  if (isNaN(d.getTime())) return s;
  var y = d.getFullYear();
  var m = String(d.getMonth() + 1).padStart(2, '0');
  var day = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}

function formatColumnName(key) {
  if (!key || typeof key !== 'string') return key;
  return key.replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
}

function isUrl(str) {
  if (str == null || typeof str !== 'string') return false;
  var s = str.trim();
  return /^https?:\/\//i.test(s) || /^www\./i.test(s);
}

function formatTableCellValue(val, key) {
  if (val == null || val === '') return '—';
  if (key === 'timestamp') return formatNoteDate(val) || val;
  var s = String(val).trim();
  if (isUrl(s)) {
    var href = /^https?:\/\//i.test(s) ? s : 'https://' + s;
    return React.createElement('a', { href: href, target: '_blank', rel: 'noopener noreferrer', className: 'visualization-table-link' }, s);
  }
  return s;
}

/** Parse items (JSON array or array) and render as bullet points: description (quantity × price) */
function formatItemsAsPoints(val) {
  if (val == null) return '—';
  var arr = val;
  if (typeof val === 'string') {
    var s = val.trim();
    if (s === '') return '—';
    try {
      arr = JSON.parse(s);
    } catch (e) {
      return s;
    }
  }
  if (!Array.isArray(arr) || arr.length === 0) return '—';
  var points = arr.map(function (item, idx) {
    var desc = (item && (item.description || item.name || item.title)) || '—';
    var qty = item && item.quantity != null ? item.quantity : '';
    var price = item && item.price != null ? item.price : '';
    var part = desc;
    if (qty !== '' || price !== '') {
      part = part + (qty !== '' && price !== '' ? ' (' + qty + ' × ' + price + ')' : qty !== '' ? ' (qty: ' + qty + ')' : ' (' + price + ')');
    }
    return React.createElement('li', { key: idx, className: 'visualization-items-point' }, part);
  });
  return React.createElement('ul', { className: 'visualization-items-point-list' }, points);
}

function isTimestampToday(timestamp) {
  if (timestamp == null || timestamp === '') return false;
  var s = String(timestamp).trim();
  var d = new Date(s);
  if (!isNaN(d.getTime())) {
    var now = new Date();
    return d.getUTCFullYear() === now.getUTCFullYear() &&
      d.getUTCMonth() === now.getUTCMonth() &&
      d.getUTCDate() === now.getUTCDate();
  }
  var match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    var now = new Date();
    return match[1] === String(now.getUTCFullYear()) &&
      match[2] === String(now.getUTCMonth() + 1).padStart(2, '0') &&
      match[3] === String(now.getUTCDate()).padStart(2, '0');
  }
  return false;
}

function noteToTile(item, panelId, index) {
  var title = item.content || item.title || (typeof item === 'string' ? item : 'Note');
  if (typeof title !== 'string') title = String(title);
  title = title.length > 80 ? title.slice(0, 80) + '…' : title;
  return {
    id: panelId + '-' + index + '-' + String(index),
    title: title,
    designation: null,
    company: item.context || null,
    address: formatNoteDate(item.timestamp) || null,
    phone: null,
    s3_link: item.s3_link || null,
  };
}

function taskToTile(item, panelId, index) {
  var title = item.content || item.text || 'Task';
  if (typeof title !== 'string') title = String(title);
  title = title.length > 80 ? title.slice(0, 80) + '…' : title;
  return {
    id: panelId + '-' + index + '-' + String(index),
    title: title,
    designation: null,
    company: item.contact_name || null,
    address: item.ai_reason || null,
    phone: formatNoteDate(item.timestamp) || (item.timestamp != null ? String(item.timestamp) : null),
    s3_link: null,
  };
}

function mapTasksToItems(data) {
  if (!data || !data.data) return EMPTY_BOARD_TASKS;
  var items = data.data.text || [];
  return { items: items };
}

function mapContactsToBoard(data) {
  if (!data || !data.data) return EMPTY_BOARD;
  var d = data.data;
  return {
    personal: (d.personal || []).map(function (item, i) { return contactToTile(item, 'personal', i); }),
    official: (d.dr || []).map(function (item, i) { return contactToTile(item, 'official', i); }),
    business: (d.business || []).map(function (item, i) { return contactToTile(item, 'business', i); }),
    other: (d.other || []).map(function (item, i) { return contactToTile(item, 'other', i); }),
  };
}

function mapContactsToBoardThreeColumns(data) {
  if (!data || !data.data) return EMPTY_BOARD_CONTACTS;
  var d = data.data;
  var otherItems = (d.dr || []).concat(d.other || []);
  return {
    personal: (d.personal || []).map(function (item, i) { return contactToTile(item, 'personal', i); }),
    business: (d.business || []).map(function (item, i) { return contactToTile(item, 'business', i); }),
    other: otherItems.map(function (item, i) { return contactToTile(item, 'other', i); }),
  };
}

function mapExpensesToBoard(data) {
  if (!data || !data.data) return EMPTY_BOARD_EXPENSES;
  var d = data.data;
  var medical = [];
  var food = [];
  var vehicle = [];
  var other = [];
  if (Array.isArray(d)) {
    d.forEach(function (item, i) {
      var cat = expenseCategoryFromItem(item);
      var tile = expenseToTile(item, cat, (cat === 'medical' ? medical : cat === 'food' ? food : cat === 'vehicle' ? vehicle : other).length);
      if (cat === 'medical') medical.push(tile);
      else if (cat === 'food') food.push(tile);
      else if (cat === 'vehicle') vehicle.push(tile);
      else other.push(tile);
    });
  } else {
    (d.medical || []).forEach(function (item, i) { medical.push(expenseToTile(item, 'medical', i)); });
    (d.food || []).forEach(function (item, i) { food.push(expenseToTile(item, 'food', i)); });
    (d.vehicle || []).forEach(function (item, i) { vehicle.push(expenseToTile(item, 'vehicle', i)); });
    (d.other || []).forEach(function (item, i) { other.push(expenseToTile(item, 'other', i)); });
  }
  return { medical: medical, food: food, vehicle: vehicle, other: other };
}

function mapNotesToItems(data) {
  if (!data || !data.data) return EMPTY_BOARD_NOTES;
  var d = data.data;
  var items = (d.unknown || []).concat(d.link || []);
  return { items: items };
}

function mapJustSavedBoard(data) {
  var out = {
    personal: [],
    official: [],
    business: [],
    other: [],
  };
  var contacts = data.contacts;
  if (contacts && contacts.data) {
    var c = contacts.data;
    out.personal = (c.personal || []).filter(function (item) { return isTimestampToday(item.timestamp); }).map(function (item, i) { return contactToTile(item, 'personal', i); });
    out.official = (c.dr || []).filter(function (item) { return isTimestampToday(item.timestamp); }).map(function (item, i) { return contactToTile(item, 'official', i); });
    out.business = (c.business || []).filter(function (item) { return isTimestampToday(item.timestamp); }).map(function (item, i) { return contactToTile(item, 'business', i); });
    out.other = (c.other || []).filter(function (item) { return isTimestampToday(item.timestamp); }).map(function (item, i) { return contactToTile(item, 'other', i); });
  }
  var notes = data.notes;
  if (notes && notes.data) {
    var n = notes.data;
    var noteList = (n.unknown || []).concat(n.link || []);
    var todayNotes = noteList.filter(function (item) { return isTimestampToday(item.timestamp); });
    var otherStart = out.other.length;
    todayNotes.forEach(function (item, i) {
      out.other.push(noteToTile(item, 'other', otherStart + i));
    });
  }
  return out;
}

/** New just-saved API shape: panels Tasks, Contacts, Expenses, Notes with card tiles (only data present in just-saved) */
function mapJustSavedToPanels(justSaved) {
  if (!justSaved || !justSaved.data) return EMPTY_JUST_SAVED_PANELS;
  var data = justSaved.data;
  var out = { tasks: [], contacts: [], expenses: [], notes: [] };

  if (data.tasks && data.tasks.data) {
    var taskList = data.tasks.data.text || (Array.isArray(data.tasks.data) ? data.tasks.data : []);
    out.tasks = taskList.map(function (item, i) { return taskToTile(item, 'tasks', i); });
  }

  if (data.contacts && data.contacts.data) {
    var contactRows = [];
    var c = data.contacts.data;
    ['personal', 'business', 'other', 'dr', 'official'].forEach(function (key) {
      (c[key] || []).forEach(function (item) { contactRows.push(item); });
    });
    out.contacts = contactRows.map(function (item, i) { return contactToTile(item, 'contacts', i); });
  }

  if (data.expenses && data.expenses.data) {
    var expenseRows = [];
    var e = data.expenses.data;
    if (Array.isArray(e)) {
      expenseRows = e.slice();
    } else {
      ['medical', 'food', 'vehicle', 'other'].forEach(function (key) {
        (e[key] || []).forEach(function (item) { expenseRows.push(item); });
      });
    }
    out.expenses = expenseRows.map(function (item, i) { return expenseToTile(item, 'expenses', i); });
  }

  if (data.notes && data.notes.data) {
    var noteRows = [];
    var n = data.notes.data;
    Object.keys(n || {}).forEach(function (key) {
      (n[key] || []).forEach(function (item) { noteRows.push(item); });
    });
    out.notes = noteRows.map(function (item, i) { return noteToTile(item, 'notes', i); });
  }

  return out;
}

function getTableColumnsFromRows(rows) {
  var keys = [];
  rows.forEach(function (row) {
    if (row && typeof row === 'object') {
      Object.keys(row).forEach(function (k) {
        if (keys.indexOf(k) === -1) keys.push(k);
      });
    }
  });
  return keys;
}

function parseApiIntoBoards(json) {
  var data = json.data || {};
  var justSavedPayload = json['just-saved'] || (data && data['just-saved']);
  var justSaved = justSavedPayload ? mapJustSavedToPanels(justSavedPayload) : mapJustSavedBoard(data);
  return {
    tasks: data.tasks ? mapTasksToItems(data.tasks) : EMPTY_BOARD_TASKS,
    contacts: mapContactsToBoardThreeColumns(data.contacts),
    expenses: mapExpensesToBoard(data.expenses),
    notes: mapNotesToItems(data.notes),
    just_saved: justSaved,
  };
}

function Visualization() {
  const { user } = useAuth();
  const [boards, setBoards] = useState({
    tasks: { ...EMPTY_BOARD_TASKS },
    contacts: { ...EMPTY_BOARD_CONTACTS },
    expenses: { ...EMPTY_BOARD_EXPENSES },
    notes: { ...EMPTY_BOARD_NOTES },
    just_saved: { ...EMPTY_JUST_SAVED_PANELS },
  });
  const [activeMenu, setActiveMenu] = useState('contacts');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragSourcePanel, setDragSourcePanel] = useState(null);
  var mobileNumber = user && user.phone_number ? user.phone_number : null;

  useEffect(function () {
    if (!mobileNumber) {
      setError('Phone number not found in session.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetch(VIZ_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile_number: mobileNumber }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to load visualization data');
        return res.json();
      })
      .then(function (json) {
        setTotalCount(json.total_count || 0);
        setBoards(parseApiIntoBoards(json));
      })
      .catch(function (err) {
        setError(err.message || 'Something went wrong');
        setBoards({
          tasks: { ...EMPTY_BOARD_TASKS },
          contacts: { ...EMPTY_BOARD_CONTACTS },
          expenses: { ...EMPTY_BOARD_EXPENSES },
          notes: { ...EMPTY_BOARD_NOTES },
          just_saved: { ...EMPTY_JUST_SAVED_PANELS },
        });
      })
      .finally(function () {
        setLoading(false);
      });
  }, [mobileNumber]);

  var board = boards[activeMenu] || EMPTY_BOARD;
  var isJustSavedPanels = activeMenu === 'just_saved' && board && Array.isArray(board.tasks);

  const updateBoard = useCallback(function (updater) {
    setBoards(function (prev) {
      var next = { ...prev };
      next[activeMenu] = updater(next[activeMenu] || EMPTY_BOARD);
      return next;
    });
  }, [activeMenu]);

  const handleDragStart = useCallback(function (e, tile, panelId) {
    setDraggedItem(tile);
    setDragSourcePanel(panelId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', tile.id);
    e.dataTransfer.setData('application/json', JSON.stringify({ tile, panelId }));
    if (e.target) e.target.classList.add('visualization-tile--dragging');
  }, []);

  const handleDragEnd = useCallback(function (e) {
    setDraggedItem(null);
    setDragSourcePanel(null);
    if (e.target) e.target.classList.remove('visualization-tile--dragging');
  }, []);

  const handleDragOver = useCallback(function (e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (e.currentTarget) e.currentTarget.classList.add('visualization-panel--drag-over');
  }, []);

  const handleDragLeave = useCallback(function (e) {
    if (e.currentTarget) e.currentTarget.classList.remove('visualization-panel--drag-over');
  }, []);

  const handleDrop = useCallback(function (e, targetPanelId) {
    e.preventDefault();
    if (e.currentTarget) e.currentTarget.classList.remove('visualization-panel--drag-over');
    var raw = e.dataTransfer.getData('application/json');
    if (!raw || !draggedItem || !dragSourcePanel) return;
    try {
      var parsed = JSON.parse(raw);
      var sourcePanelId = parsed.panelId;
      if (sourcePanelId === targetPanelId) return;

      updateBoard(function (prev) {
        var sourceList = (prev[sourcePanelId] || []).filter(function (t) { return t.id !== draggedItem.id; });
        var targetList = [...(prev[targetPanelId] || []), draggedItem];
        return {
          ...prev,
          [sourcePanelId]: sourceList,
          [targetPanelId]: targetList,
        };
      });
    } catch (_) {}
    setDraggedItem(null);
    setDragSourcePanel(null);
  }, [draggedItem, dragSourcePanel, updateBoard]);

  function renderTile(tile, panelId) {
    if (activeMenu === 'contacts' || (activeMenu === 'just_saved' && panelId === 'contacts')) {
      return (
        <div
          key={tile.id}
          className="visualization-tile visualization-tile--contact"
          draggable
          onDragStart={function (e) { handleDragStart(e, tile, panelId); }}
          onDragEnd={handleDragEnd}
        >
          <div className="visualization-contact-card-accent" />
          <div className="visualization-contact-card-body">
            <div className="visualization-contact-card-left">
              <div className="visualization-contact-card-company">
                {tile.company || '—'}
              </div>
            </div>
            <div className="visualization-contact-card-right">
              <h3 className="visualization-contact-card-name">{tile.title}</h3>
              {tile.designation ? (
                <p className="visualization-contact-card-position">{tile.designation}</p>
              ) : null}
              <div className="visualization-contact-card-details">
                {tile.company ? (
                  <div className="visualization-contact-card-row">
                    <span className="visualization-contact-card-icon"><BsBuilding size={14} /></span>
                    <span>{tile.company}</span>
                  </div>
                ) : null}
                {tile.phone ? (
                  <div className="visualization-contact-card-row">
                    <span className="visualization-contact-card-icon"><BsTelephone size={14} /></span>
                    <span>{tile.phone}</span>
                  </div>
                ) : null}
                {tile.address ? (
                  <div className="visualization-contact-card-row">
                    <span className="visualization-contact-card-icon"><BsGeoAlt size={14} /></span>
                    <span>{tile.address}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="visualization-contact-card-accent visualization-contact-card-accent--bottom" />
        </div>
      );
    }
    return (
      <div
        key={tile.id}
        className="visualization-tile"
        draggable
        onDragStart={function (e) { handleDragStart(e, tile, panelId); }}
        onDragEnd={handleDragEnd}
      >
        {tile.s3_link ? (
          <div className="visualization-tile-image-wrap">
            <img src={tile.s3_link} alt="" className="visualization-tile-image" />
          </div>
        ) : null}
        <p className="visualization-tile-title">
          {tile.title}
          {tile.designation ? (
            <span className="visualization-tile-designation">{tile.designation}</span>
          ) : null}
        </p>
        {tile.company ? (
          <p className="visualization-tile-row">
            <span className="visualization-tile-field">
              {activeMenu === 'just_saved' ? (panelId === 'expenses' ? 'Amount' : panelId === 'notes' ? 'Context' : panelId === 'tasks' ? 'Contact' : 'Company') : activeMenu === 'expenses' ? 'Amount' : activeMenu === 'notes' ? 'Context' : 'Company'}
            </span>
            {tile.company}
          </p>
        ) : null}
        {tile.address ? (
          <p className="visualization-tile-row">
            <span className="visualization-tile-field">
              {activeMenu === 'just_saved' ? (panelId === 'expenses' ? 'Description' : panelId === 'notes' ? 'Date' : panelId === 'tasks' ? 'AI Reason' : 'Address') : activeMenu === 'expenses' ? 'Description' : activeMenu === 'notes' ? 'Date' : 'Address'}
            </span>
            {tile.address}
          </p>
        ) : null}
        {tile.phone ? (
          <p className="visualization-tile-row">
            <span className="visualization-tile-field">{activeMenu === 'just_saved' && panelId === 'tasks' ? 'Date' : activeMenu === 'expenses' ? 'Contact' : 'Phone'}</span>
            {tile.phone}
          </p>
        ) : null}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="visualization-page">
        <header className="visualization-page-header">
          <h1 className="visualization-page-title">Vizualization Dashboard</h1>
          <p className="visualization-board-subtitle">Loading…</p>
        </header>
      </div>
    );
  }

  if (error) {
    return (
      <div className="visualization-page">
        <header className="visualization-page-header">
          <h1 className="visualization-page-title">Vizualization Dashboard</h1>
          <p className="visualization-board-subtitle visualization-board-error">{error}</p>
        </header>
      </div>
    );
  }

  return (
    <div className="visualization-page">
      <header className="visualization-page-header">
        <h1 className="visualization-page-title">Vizualization Dashboard</h1>
        <p className="visualization-board-subtitle">Drag tiles between panels as needed. Total: {totalCount}</p>
      </header>
      <div className="visualization-layout">
        <aside className="visualization-sidebar">
          <div className="visualization-sidebar-inner">
            <h2 className="visualization-sidebar-title">Categories</h2>
            <nav className="visualization-nav">
              {MENU_ITEMS.map(function (item) {
                var isActive = activeMenu === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={'visualization-nav-item' + (isActive ? ' visualization-nav-item--active' : '')}
                    onClick={function () { setActiveMenu(item.id); }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        <div className="visualization-main">
          <nav className="visualization-mobile-tabs" aria-label="Category">
            {MENU_ITEMS.map(function (item) {
              var isActive = activeMenu === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={'visualization-nav-item' + (isActive ? ' visualization-nav-item--active' : '')}
                  onClick={function () { setActiveMenu(item.id); }}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>
          <div className="visualization-board">
            {activeMenu === 'tasks' ? (
              <div className="visualization-tasks-table-wrap">
                <table className="visualization-tasks-table">
                  <thead>
                    <tr>
                      <th>Content</th>
                      <th>Contact</th>
                      <th>AI Reason</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(board.items || []).map(function (task, i) {
                      return (
                        <tr key={'task-' + i}>
                          <td>{formatTableCellValue(task.content, 'content')}</td>
                          <td>{formatTableCellValue(task.contact_name, 'contact_name')}</td>
                          <td>{formatTableCellValue(task.ai_reason, 'ai_reason')}</td>
                          <td>{formatTableCellValue(task.timestamp, 'timestamp')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {(board.items || []).length === 0 ? (
                  <p className="visualization-tasks-empty">No tasks</p>
                ) : null}
              </div>
            ) : activeMenu === 'notes' ? (
              (function () {
                var noteItems = board.items || [];
                var keys = [];
                noteItems.forEach(function (item) {
                  Object.keys(item).forEach(function (k) {
                    if (keys.indexOf(k) === -1) keys.push(k);
                  });
                });
                return (
                  <div className="visualization-tasks-table-wrap">
                    <table className="visualization-tasks-table">
                      <thead>
                        <tr>
                          {keys.map(function (k) { return <th key={k}>{formatColumnName(k)}</th>; })}
                        </tr>
                      </thead>
                      <tbody>
                        {noteItems.map(function (note, i) {
                          return (
                            <tr key={'note-' + i}>
                              {keys.map(function (k) {
                                return <td key={k}>{formatTableCellValue(note[k], k)}</td>;
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {noteItems.length === 0 ? (
                      <p className="visualization-tasks-empty">No notes</p>
                    ) : null}
                  </div>
                );
              })()
            ) : isJustSavedPanels ? (
              <div className="visualization-panels">
                {JUST_SAVED_PANELS.filter(function (panel) { return (board[panel.id] || []).length > 0; }).map(function (panel) {
                  var tiles = board[panel.id] || [];
                  return (
                    <div key={panel.id} className="visualization-panel">
                      <div className="visualization-panel-header">
                        <span className="visualization-panel-title">{panel.label}</span>
                        <span className="visualization-panel-count">{tiles.length}</span>
                      </div>
                      <div className="visualization-panel-tiles">
                        {tiles.map(function (tile) { return renderTile(tile, panel.id); })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="visualization-panels">
                {(activeMenu === 'contacts' ? CONTACT_COLUMNS : activeMenu === 'expenses' ? EXPENSE_CATEGORIES : KANBAN_COLUMNS).map(function (panel) {
                  var tiles = board[panel.id] || [];
                  return (
                    <div
                      key={panel.id}
                      className="visualization-panel"
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={function (e) { handleDrop(e, panel.id); }}
                    >
                      <div className="visualization-panel-header">
                        <span className="visualization-panel-title">{panel.label}</span>
                        <span className="visualization-panel-count">{tiles.length}</span>
                      </div>
                      <div className="visualization-panel-tiles">
                        {tiles.map(function (tile) { return renderTile(tile, panel.id); })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Visualization;
